import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, Pencil, Search } from "lucide-react";
import ReservationHeader from "../../Commun/ReservationHeader";
import ReservationSidebar from "../../Commun/ReservationSidebar";
import "./Manage.css";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { useProperty } from "../../../PropertyContext";
import {
  fetchReservationBlocks,
  updateReservationBlock,
} from "../../../Redux/Slices/Reservation/Block/Reservationblockslice ";

import MarketPopup from "../Create Block/Popups/MarketPopup";
import SourcePopup from "../Create Block/Popups/SourcePopup";
import AccountPopup from "../Create Block/Popups/AccountPopup";
import RateCodePopup from "../Create Block/Popups/RateCodePopup";
import UpdateBlockPopup from "./UpdatePopup/UpdateBlockPopup";

const fields = [
  { label: "Block Name", name: "blockName" },
  { label: "End Date From", name: "endDateFrom", type: "date" },
  { label: "Group", name: "group" },
  { label: "Market", name: "market", type: "search" },
  { label: "Created On", name: "createdOn", type: "date" },

  { label: "Block Code", name: "blockCode" },
  { label: "End Date To", name: "endDateTo", type: "date" },
  { label: "Account", name: "account", type: "search" },
  { label: "Source", name: "source", type: "search" },
  { label: "Created By", name: "createdBy", type: "search" },

  { label: "Block ID", name: "blockId" },
  { label: "Stay Date", name: "stayDate", type: "date" },
  { label: "Contact", name: "contact" },
  { label: "Tour Code", name: "tourCode" },
  { label: "External Reference", name: "externalReference" },

  { label: "Start Date From", name: "startDateFrom", type: "date" },
  { label: "Block Status", name: "blockStatus", type: "select" },
  { label: "Rate Code", name: "rateCode", type: "search" },
  { label: "Owner", name: "owner" },
  { label: "Start Date To", name: "startDateTo", type: "date" },
];

const basicFieldNames = [
  "blockName",
  "blockCode",
  "blockStatus",
  "startDateFrom",
  "startDateTo",
];

const formatDate = (value) => {
  if (!value) return "";

  const [year, month, day] = value.split("-");

  return `${day}-${month}-${year}`;
};

const FilterField = ({
  label,
  name,
  type,
  value,
  onChange,
  onIconClick,
}) => {
  const pickerRef = useRef(null);

  const openPicker = () => {
    if (pickerRef.current?.showPicker) {
      pickerRef.current.showPicker();
    } else {
      pickerRef.current?.click();
    }
  };

  return (
    <label className="manage-blocks-field">
      <span>{label}</span>

      <span className="manage-blocks-input-wrap">
        {type === "select" ? (
          <select
            name={name}
            aria-label={label}
            value={value ?? ""}
            onChange={onChange}
          >
            <option value="">All</option>
            <option value="TEN">TEN</option>
            <option value="DEF">DEF</option>
            <option value="ACT">ACT</option>
            <option value="CXL">CXL</option>
          </select>
        ) : (
          <input
            type="text"
            name={name}
            placeholder={type === "date" ? "DD-MM-YYYY" : ""}
            aria-label={label}
            value={type === "date" ? formatDate(value) : value ?? ""}
            onChange={type !== "date" ? onChange : undefined}
            onClick={type === "date" ? openPicker : undefined}
            readOnly={type === "date"}
          />
        )}

        {type === "date" && (
          <>
            <input
              ref={pickerRef}
              type="date"
              className="manage-native-date-picker"
              value={value ?? ""}
              onChange={onChange}
              tabIndex={-1}
              aria-hidden="true"
            />

            <button
              type="button"
              className="manage-date-button"
              onClick={openPicker}
              aria-label={`Choose ${label}`}
            >
              <CalendarDays aria-hidden="true" />
            </button>
          </>
        )}

        {type === "search" && onIconClick && (
          <button
            type="button"
            className="manage-search-button"
            onClick={onIconClick}
            aria-label={`Search ${label}`}
          >
            <Search aria-hidden="true" />
          </button>
        )}
      </span>
    </label>
  );
};

export const ManageBlocks = () => {
  const dispatch = useDispatch();

  const { selectedProperty } = useProperty();

  const {
    blocks,
    listLoading,
    listError,
    pagination,
    updateLoading,
  } = useSelector((state) => state.reservationBlock);

  const [activeLookup, setActiveLookup] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);

  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);

  // =====================================================
  // DEFAULT PAGE SIZE 10
  // =====================================================
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState({
    blockName: "",
    blockCode: "",
    blockStatus: "",
    startDateFrom: "",
    startDateTo: "",
    account: "",
    market: "",
    rateCode: "",
    source: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    BlockName: "",
    BlockCode: "",
    BlockStatus: "",
    StartDateFrom: "",
    StartDateTo: "",
  });

  // =====================================================
  // LOAD BLOCKS
  // =====================================================

  const loadBlocks = (
    pageNumber,
    searchFilters = appliedFilters,
    selectedPageSize = pageSize
  ) => {
    if (!selectedProperty?.PropertyID) return;

    dispatch(
      fetchReservationBlocks({
        PropertyID: selectedProperty.PropertyID,
        PageNumber: pageNumber,

        // By default 10
        PageSize: selectedPageSize,

        ...searchFilters,
      })
    );
  };

  // =====================================================
  // PROPERTY CHANGE
  // =====================================================

  useEffect(() => {
    if (selectedProperty?.PropertyID) {
      loadBlocks(
        1,
        {
          BlockName: "",
          BlockCode: "",
          BlockStatus: "",
          StartDateFrom: "",
          StartDateTo: "",
        },
        10
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.PropertyID]);

  const closeLookup = () => {
    setActiveLookup(null);
  };

  const updateLookupValue = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const selectLookupValue = (name, value) => {
    updateLookupValue(name, value);
    closeLookup();
  };

  // =====================================================
  // VISIBLE SEARCH FIELDS
  // =====================================================

  const visibleFields = isAdvancedSearch
    ? fields
    : basicFieldNames
        .map((name) => fields.find((field) => field.name === name))
        .filter(Boolean);

  // =====================================================
  // SEARCH
  // =====================================================

  const handleSearch = (event) => {
    event.preventDefault();

    const searchFilters = {
      BlockName: filters.blockName.trim(),
      BlockCode: filters.blockCode.trim(),
      BlockStatus: filters.blockStatus,
      StartDateFrom: filters.startDateFrom,
      StartDateTo: filters.startDateTo,
    };

    setAppliedFilters(searchFilters);

    loadBlocks(1, searchFilters, pageSize);
  };

  // =====================================================
  // RESET
  // =====================================================

  const handleReset = () => {
    setFilters({
      blockName: "",
      blockCode: "",
      blockStatus: "",
      startDateFrom: "",
      startDateTo: "",
      account: "",
      market: "",
      rateCode: "",
      source: "",
    });

    const emptyFilters = {
      BlockName: "",
      BlockCode: "",
      BlockStatus: "",
      StartDateFrom: "",
      StartDateTo: "",
    };

    setAppliedFilters(emptyFilters);

    loadBlocks(1, emptyFilters, pageSize);
  };

  // =====================================================
  // PAGE SIZE CHANGE
  // =====================================================

  const handlePageSizeChange = (event) => {
    const newPageSize = Number(event.target.value);

    setPageSize(newPageSize);

    // Page size change hone par first page se start
    loadBlocks(1, appliedFilters, newPageSize);
  };

  // =====================================================
  // UPDATE BLOCK
  // =====================================================

  const handleUpdateBlock = async (postData) => {
    if (!postData.OPID) {
      swal("OPID not found for this block.", {
        icon: "error",
      });

      return;
    }

    try {
      const response = await dispatch(
        updateReservationBlock(postData)
      ).unwrap();

      setSelectedBlock(null);

      loadBlocks(
        pagination.pageNumber || 1,
        appliedFilters,
        pageSize
      );

      swal(response?.Message || "Block updated successfully.", {
        icon: "success",
      });
    } catch (error) {
      swal(
        typeof error === "string"
          ? error
          : "Failed to update block.",
        {
          icon: "error",
        }
      );
    }
  };

  const displayDate = (value) => {
    if (!value) return "-";

    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  return (
    <div className="wrapper">
      <ReservationSidebar />

      <div className="main-panel">
        <ReservationHeader />

        <div className="container">
          <div className="page-inner manage-blocks-page">

            {/* PAGE HEADER */}

            <header className="manage-blocks-title">
              <div className="d-flex justify-content-between align-items-center">
                <h1>Manage Blocks</h1>

                <NavLink
                  to="/Reservation/CreateBlock"
                  className="text-primary"
                >
                  Create Block
                </NavLink>
              </div>
            </header>

            {/* SEARCH CARD */}

            <div className="card">
              <div className="card-body">

                <div className="manage-blocks-panel-heading">
                  <div>
                    <Search aria-hidden="true" />

                    <span>
                      {isAdvancedSearch
                        ? "ADVANCED SEARCH"
                        : "BASIC SEARCH"}
                    </span>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        setIsAdvancedSearch(
                          (current) => !current
                        )
                      }
                    >
                      {isAdvancedSearch
                        ? "Go to Basic Search"
                        : "Go to Advanced Search"}
                    </button>
                  </div>
                </div>

                <form
                  onSubmit={handleSearch}
                  onReset={handleReset}
                >
                  <div className="manage-blocks-grid">

                    {visibleFields.map((field) => {
                      const hasLookup = [
                        "account",
                        "market",
                        "rateCode",
                        "source",
                      ].includes(field.name);

                      return (
                        <FilterField
                          key={field.name}
                          {...field}
                          value={filters[field.name] ?? ""}
                          onChange={(event) =>
                            updateLookupValue(
                              field.name,
                              event.target.value
                            )
                          }
                          onIconClick={
                            hasLookup
                              ? () =>
                                  setActiveLookup(field.name)
                              : undefined
                          }
                        />
                      );
                    })}

                    {isAdvancedSearch && (
                      <label className="manage-blocks-checkbox">
                        <input type="checkbox" />

                        <span>
                          Display Catering Only (?)
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="manage-blocks-actions">

                    <button
                      className="manage-blocks-clear"
                      type="reset"
                    >
                      Clear Filters
                    </button>

                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                    >
                      <Search aria-hidden="true" />
                      Search Blocks
                    </button>
                  </div>
                </form>

              </div>
            </div>

            {/* BLOCK LIST */}

            <div className="card manage-blocks-list-card">

              <div className="card-header manage-blocks-list-header">
                <h4 className="card-title mb-0">
                  Block List
                </h4>
              </div>

              <div className="card-body p-0">

                {/* PAGE SIZE */}

                <div className="manage-blocks-length">
                  <label>
                    Show

                    <select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      aria-label="Rows per page"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>

                    entries
                  </label>
                </div>

                {/* TABLE */}

                <div className="manage-blocks-table-wrap">

                  <table className="table table-bordered table-striped table-hover mb-0">

                    <thead>
                      <tr>
                        <th style={{ width: "70px" }}>
                          #Sr
                        </th>

                        <th>Block Name</th>

                        <th style={{ width: "114px" }}>
                          Block Code
                        </th>

                        <th style={{ width: "78px" }}>
                          Status
                        </th>

                        <th style={{ width: "104px" }}>
                          Start Date
                        </th>

                        <th style={{ width: "104px" }}>
                          End Date
                        </th>

                        <th style={{ width: "114px" }}>
                          Market Code
                        </th>

                        <th style={{ width: "114px" }}>
                          Source Code
                        </th>

                        <th style={{ width: "104px" }}>
                          Res Type
                        </th>

                        <th style={{ width: "104px" }}>
                          Rate Code
                        </th>

                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>

                      {listLoading ? (
                        <tr>
                          <td
                            colSpan="11"
                            className="manage-blocks-message"
                          >
                            Loading blocks...
                          </td>
                        </tr>
                      ) : listError ? (
                        <tr>
                          <td
                            colSpan="11"
                            className="manage-blocks-message text-danger"
                          >
                            {listError}
                          </td>
                        </tr>
                      ) : blocks.length === 0 ? (
                        <tr>
                          <td
                            colSpan="11"
                            className="manage-blocks-message"
                          >
                            No Results to Display
                          </td>
                        </tr>
                      ) : (
                        blocks.map((block, index) => (
                          <tr key={block.BlockID}>

                            <td>
                              {(pagination.pageNumber - 1) *
                                pageSize +
                                index +
                                1}
                            </td>

                            <td>
                              {block.BlockName || ""}
                            </td>

                            <td>
                              {block.BlockCode || ""}
                            </td>

                            <td>
                              {block.BlockStatus || ""}
                            </td>

                            <td>
                              {displayDate(
                                block.StartDate
                              )}
                            </td>

                            <td>
                              {displayDate(
                                block.EndDate
                              )}
                            </td>

                            <td>
                              {block.MarketCode || ""}
                            </td>

                            <td>
                              {block.SourceCode || ""}
                            </td>

                            <td>
                              {block.ReservationType || ""}
                            </td>

                            <td>
                              {block.RateCode || ""}
                            </td>

                            <td className="manage-blocks-action-cell">
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() =>
                                  setSelectedBlock(block)
                                }
                              >
                                <Pencil
                                  aria-hidden="true"
                                  size={16}
                                />

                                <span className="visually-hidden">
                                  Edit{" "}
                                  {block.BlockName}
                                </span>
                              </button>
                            </td>

                          </tr>
                        ))
                      )}

                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}

                {!listLoading &&
                  !listError &&
                  pagination.totalRecords > 0 && (
                    <div className="manage-blocks-pagination">

                      <span>
                        Showing{" "}
                        {(pagination.pageNumber - 1) *
                          pageSize +
                          1}{" "}
                        to{" "}
                        {Math.min(
                          pagination.pageNumber *
                            pageSize,
                          pagination.totalRecords
                        )}{" "}
                        of{" "}
                        {pagination.totalRecords}{" "}
                        entries
                      </span>

                      <div>

                        <button
                          type="button"
                          disabled={
                            !pagination.hasPreviousPage
                          }
                          onClick={() =>
                            loadBlocks(
                              pagination.pageNumber - 1,
                              appliedFilters,
                              pageSize
                            )
                          }
                        >
                          Previous
                        </button>

                        <span>
                          Page{" "}
                          {pagination.pageNumber} of{" "}
                          {pagination.totalPages}
                        </span>

                        <button
                          type="button"
                          disabled={
                            !pagination.hasNextPage
                          }
                          onClick={() =>
                            loadBlocks(
                              pagination.pageNumber + 1,
                              appliedFilters,
                              pageSize
                            )
                          }
                        >
                          Next
                        </button>

                      </div>
                    </div>
                  )}

              </div>
            </div>

            {/* MARKET POPUP */}

            <MarketPopup
              show={activeLookup === "market"}
              onClose={closeLookup}
              onSelect={(option) =>
                selectLookupValue(
                  "market",
                  option.Code
                )
              }
            />

            {/* SOURCE POPUP */}

            <SourcePopup
              show={activeLookup === "source"}
              onClose={closeLookup}
              onSelect={(option) =>
                selectLookupValue(
                  "source",
                  option.Code
                )
              }
            />

            {/* ACCOUNT POPUP */}

            <AccountPopup
              show={activeLookup === "account"}
              onClose={closeLookup}
              onSelect={(option) =>
                selectLookupValue(
                  "account",
                  option.TA_Name
                )
              }
            />

            {/* RATE CODE POPUP */}

            <RateCodePopup
              show={activeLookup === "rateCode"}
              onClose={closeLookup}
              onSelect={(option) =>
                selectLookupValue(
                  "rateCode",
                  option.Code
                )
              }
            />

            {/* UPDATE POPUP */}

            <UpdateBlockPopup
              show={Boolean(selectedBlock)}
              block={selectedBlock}
              onClose={() =>
                setSelectedBlock(null)
              }
              onUpdate={handleUpdateBlock}
              updating={updateLoading}
            />

          </div>
        </div>
      </div>
    </div>
  );
};